import { useState } from 'react';
import { Clock, ChevronDown } from 'lucide-react';
import { useTheme } from '../../zustand/ThemeStore';
import DualRangeSlider from './DualRangeSlider';

interface DurationRangeFilterProps {
  value: string;
  onChange: (value: string) => void;
  customMinDuration?: number;
  customMaxDuration?: number;
  onCustomDurationChange: (min: number, max: number) => void;
}

const DURATION_OPTIONS = [
  { label: 'Any Duration', value: '' },
  { label: 'Short (< 5 min)', value: 'short' },
  { label: 'Medium (5-30 min)', value: 'medium' },
  { label: 'Long (30+ min)', value: 'long' },
  { label: 'Custom Range', value: 'custom' }
];

export default function DurationRangeFilter({
  value,
  onChange,
  customMinDuration,
  customMaxDuration,
  onCustomDurationChange
}: DurationRangeFilterProps) {
  const isDarkMode = useTheme((state) => state.theme);
  const [isOpen, setIsOpen] = useState(false);

  const selectedOption = DURATION_OPTIONS.find(option => option.value === value);

  const handleDurationRangeChange = (range: [number, number]) => {
    onCustomDurationChange(range[0], range[1]);
  };

  const formatValue = (val: number) => {
    if (val < 60) {
      return `${val}min`;
    }
    const hours = Math.floor(val / 60);
    const mins = val % 60;
    return mins > 0 ? `${hours}h ${mins}min` : `${hours}h`;
  };

  return (
    <div className="relative">
      {/* Main Filter Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-2 px-3 py-2 text-sm border rounded-lg transition-colors ${
          isDarkMode 
            ? 'bg-[#050a0a] border-[#1a2424] text-white hover:bg-[#0a1414]' 
            : 'bg-white border-gray-200 text-[#053333] hover:bg-gray-50'
        }`}
      >
        <Clock className="w-4 h-4" />
        <span>{selectedOption?.label || 'Duration'}</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className={`absolute top-full left-0 mt-1 w-80 rounded-lg shadow-xl border z-50 ${
          isDarkMode ? 'bg-[#050a0a] border-[#1a2424]' : 'bg-white border-gray-200'
        }`}>
          <div className="p-4">
            {/* Quick Options */}
            {/* <div className="space-y-2 mb-4">
              {DURATION_OPTIONS.filter(option => option.value !== 'custom').map((option) => (
                <button
                  key={option.value}
                  onClick={() => {
                    onChange(option.value);
                    if (option.value !== 'custom') {
                      setIsOpen(false);
                    }
                  }}
                  className={`w-full text-left px-3 py-2 text-sm rounded-lg transition-colors ${
                    value === option.value
                      ? (isDarkMode ? 'bg-[#1a9f6b] text-white' : 'bg-[#1a9f6b] text-white')
                      : (isDarkMode ? 'hover:bg-zinc-700 text-zinc-300' : 'hover:bg-zinc-100 text-zinc-700')
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div> */}

            {/* Custom Range Slider */}
            <div className={`pt-4 ${isDarkMode ? 'border-[#1a2424]' : 'border-gray-200'}`}>
              <div className="flex items-center justify-between mb-4">
                <h4 className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-[#053333]'}`}>
                  Duration Range
                </h4>
                <button
                  onClick={() => {
                    onChange('custom');
                    setIsOpen(false);
                  }}
                  className={`px-4 py-1.5 text-xs font-medium rounded-lg transition-all ${
                    value === 'custom'
                      ? 'bg-gradient-to-r from-[#1a9f6b] to-[#148856] text-white shadow-md'
                      : (isDarkMode 
                          ? 'bg-[#1a2424] text-gray-300 hover:bg-[#2a3434] border border-[#1a2424]' 
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-200'
                        )
                  }`}
                >
                  Apply Range
                </button>
              </div>

              <DualRangeSlider
                min={0}
                max={120}
                value={[customMinDuration || 0, customMaxDuration || 60]}
                onChange={handleDurationRangeChange}
                step={1}
                formatValue={formatValue}
              />
            </div>
          </div>
        </div>
      )}

      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
}